import { Component } from '@theme/component';

/**
 * GOOMY · Selector de compra única vs suscripción.
 *
 * Cada tarjeta tiene un data-selling-plan (vacío = compra única). Al elegir una,
 * marcamos la activa y escribimos el valor en el input oculto name="selling_plan"
 * que vive dentro del formulario de compra, para que se mande al carrito.
 *
 * Además, escucha los cambios del selector de cantidad (evento
 * "quantity-selector:update", que burbujea hasta el formulario) y recalcula los
 * montos mostrados: precio compra única, precio suscripción, precio tachado y
 * el ahorro mensual (descuento por unidad × cantidad + ahorro de envío fijo).
 */
class DeilySubscription extends Component {
  connectedCallback() {
    super.connectedCallback?.();

    // Algunos formatos de moneda traen HTML (<span>); lo quitamos porque
    // escribimos con textContent (texto plano).
    this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    this.form = this.closest('form');

    // Escuchamos en el formulario porque el selector de cantidad es hermano,
    // no descendiente, de este componente.
    // Descuento (%) del pack activo, que llega junto con la cantidad. Se aplica
    // solo al precio de "Compra única" para que coincida con la tarjeta del pack
    // (el descuento real lo aplica el descuento automático de Shopify).
    this.packDiscount = 0;
    /** Sufijo de moneda de la barra sticky (" COP"), detectado una sola vez. */
    this.stickySuffix = null;

    this.onQuantityUpdate = (event) => {
      const qty = event?.detail?.quantity;
      this.packDiscount = Math.max(0, Number(event?.detail?.discount) || 0);
      if (qty) this.#updatePrices(qty);
    };
    const scope = this.form || document;
    scope.addEventListener('quantity-selector:update', this.onQuantityUpdate);

    // El selector de plan de la barra de compra, si el tema la muestra.
    this.planListeners = new AbortController();
    this.#bindStickyPlanSelect(this.planListeners.signal);

    // Estado inicial según la cantidad ya presente en el input.
    this.#updatePrices(this.#currentQuantity());
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    const scope = this.form || document;
    scope.removeEventListener('quantity-selector:update', this.onQuantityUpdate);
    this.planListeners?.abort();
  }

  /**
   * Lee la cantidad actual del input del formulario.
   * @returns {number}
   */
  #currentQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (
      (this.form || document).querySelector('input[name="quantity"]')
    );
    return Math.max(1, parseInt(input?.value || '1', 10) || 1);
  }

  /**
   * Recalcula y pinta todos los montos en función de la cantidad.
   * @param {number} qty
   */
  #updatePrices(qty) {
    const quantity = Math.max(1, Number(qty) || 1);

    // Precios unitarios (compra única, suscripción y tachado) × cantidad.
    for (const el of this.querySelectorAll('[data-unit-price]')) {
      const unit = Number(el.getAttribute('data-unit-price')) || 0;
      let total = unit * quantity;
      // En "Compra única" aplicamos el descuento del pack para que el precio
      // mostrado coincida con el que se cobra. Mismo cálculo entero que el
      // snippet del pack (regular - floor(regular * % / 100)), sin precio tachado.
      if (el.hasAttribute('data-onetime-price') && this.packDiscount > 0) {
        total -= Math.floor((total * this.packDiscount) / 100);
        total = this.#roundUp(total);
      }
      // Suscripción: redondeo estético hacia arriba (el precio tachado se deja
      // crudo). El descuento real lo aplica Shopify; esto es solo visual.
      if (el.hasAttribute('data-sub-now')) {
        total = this.#roundUp(total);
      }
      el.textContent = this.#formatMoney(total);
    }

    // Ahorro = descuento por unidad × cantidad + ahorro de envío (fijo por envío).
    for (const el of this.querySelectorAll('[data-discount-unit]')) {
      const discountUnit = Number(el.getAttribute('data-discount-unit')) || 0;
      const shipSave = Number(el.getAttribute('data-ship-save')) || 0;
      const total = discountUnit * quantity + shipSave;
      const label = el.getAttribute('data-savings-label') || '';
      const suffix = el.getAttribute('data-savings-suffix') || '';
      el.textContent = `${label} ${this.#formatMoney(total)} ${suffix}`.replace(/\s+/g, ' ').trim();
    }

    this.#syncStickyPrice();
  }

  /**
   * Refleja el precio de la opción activa en la barra sticky de compra.
   *
   * Liquid pinta esa barra con el precio unitario del producto, así que sin
   * esto muestra siempre $129.900 aunque el cliente haya elegido pack de 3 o
   * suscripción. Leemos el monto ya calculado de la tarjeta activa en vez de
   * repetir la matemática acá.
   */
  #syncStickyPrice() {
    const target = document.querySelector('.sticky-add-to-cart__price .price__regular .price');
    if (!target) return;

    const active = this.querySelector('.deily-sub__option.is-active');
    const source = active?.querySelector('[data-onetime-price], .deily-sub__price-now');
    if (!source) return;

    // La barra usa money_with_currency_format ("$129.900 COP") y las tarjetas
    // money_format ("$129.900"); conservamos el sufijo de moneda de la barra.
    if (this.stickySuffix == null) {
      const match = target.textContent?.match(/\s+[A-Za-z]{3}\s*$/);
      this.stickySuffix = match ? match[0].trimEnd() : '';
    }

    target.textContent = `${source.textContent?.trim() ?? ''}${this.stickySuffix}`;

    // El selector de la barra tiene que reflejar el plan elegido aunque el
    // cambio venga de las tarjetas de la ficha, no del propio selector.
    const stickySelect = /** @type {HTMLSelectElement | null} */ (
      document.querySelector('.sticky-add-to-cart__plan')
    );
    if (stickySelect) {
      stickySelect.value = active?.getAttribute('data-selling-plan') || '';
    }
  }

  /**
   * Redondea hacia arriba al múltiplo de 1.000 pesos (100000 céntimos). Solo
   * estético; debe coincidir con el snippet 'deily-round-up'.
   * @param {number} cents
   * @returns {number}
   */
  #roundUp(cents) {
    const step = 100000;
    const rem = cents % step;
    return rem > 0 ? cents - rem + step : cents;
  }

  /**
   * Formatea centavos al formato de moneda de la tienda (shop.money_format).
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const format = this.moneyFormat;
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (isNaN(number) || number == null) return '0';
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    };

    const match = format.match(placeholder);
    const token = match ? match[1] : 'amount';
    let value;
    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  }

  /**
   * Maneja el clic en una tarjeta.
   * @param {Event} event
   */
  select(event) {
    const target = event.target;
    const option = target instanceof Element ? target.closest('.deily-sub__option') : null;
    if (!option) return;
    this.#activate(option);
  }

  /**
   * Engancha el selector de plan de la barra de compra.
   *
   * Va delegado en el documento a proposito: el tema vuelve a pintar la barra
   * cuando cambia la variante, asi que un listener puesto sobre el <select>
   * se perderia en ese repintado.
   *
   * @param {AbortSignal} signal
   */
  #bindStickyPlanSelect(signal) {
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (!target.classList.contains('sticky-add-to-cart__plan')) return;

        const option = this.querySelector(
          `.deily-sub__option[data-selling-plan="${CSS.escape(target.value)}"]`
        );
        if (option) this.#activate(option);
      },
      { signal }
    );
  }

  /**
   * Marca una tarjeta como activa y sincroniza el plan elegido.
   * @param {Element} option
   */
  #activate(option) {
    for (const el of this.querySelectorAll('.deily-sub__option')) {
      const isActive = el === option;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-checked', isActive ? 'true' : 'false');
    }

    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="selling_plan"]'));
    if (input) {
      input.value = option.getAttribute('data-selling-plan') || '';
    }

    this.#syncStickyPrice();
  }
}

if (!customElements.get('deily-subscription')) {
  customElements.define('deily-subscription', DeilySubscription);
}
